import { useState, useCallback, useRef } from 'react';
import { useResizeListener } from './useResizeListener';
const sizeProp = {
    x: 'Width',
    y: 'Height',
};
/**
 * Отслеживает, что у элемента есть скроллбар, то есть он может быть проскроллен.
 *
 * @param axis Ось, относительно которой детектится скролл
 * @returns Объект с состоянием скроллбара и функцией для установки ref
 * @example
 * ```tsx
 * const ScrollableComponent = () => {
 *   const { hasScrollbar, setRef } = useHasScrollbar('y');
 *
 *   return (
 *     <div>
 *       <div
 *         ref={setRef}
 *         style={{
 *           height: '200px',
 *           overflowY: 'auto',
 *           border: '1px solid #ccc'
 *         }}
 *       >
 *         {/* Длинный контент *}
 *         {Array.from({ length: 50 }, (_, i) => (
 *           <div key={i}>Item {i + 1}</div>
 *         ))}
 *       </div>
 *       <p>Has vertical scrollbar: {hasScrollbar ? 'Yes' : 'No'}</p>
 *     </div>
 *   );
 * };
 *
 * // Использование для горизонтального скролла
 * const HorizontalScroll = () => {
 *   const { hasScrollbar, setRef } = useHasScrollbar('x');
 *
 *   return (
 *     <div>
 *       <div
 *         ref={setRef}
 *         style={{
 *           width: '300px',
 *           overflowX: 'auto',
 *           whiteSpace: 'nowrap'
 *         }}
 *       >
 *         {/* Широкий контент *}
 *         {Array.from({ length: 20 }, (_, i) => (
 *           <span key={i} style={{ display: 'inline-block', width: '100px' }}>
 *             Item {i + 1}
 *           </span>
 *         ))}
 *       </div>
 *       <p>Has horizontal scrollbar: {hasScrollbar ? 'Yes' : 'No'}</p>
 *     </div>
 *   );
 * };
 * ```
 */
export function useHasScrollbar(axis) {
    const ref = useRef();
    const [hasScrollbar, setHasScrollbar] = useState(false);
    const calcHasScrollbar = useCallback(() => {
        if (!ref.current) {
            return;
        }
        setHasScrollbar(ref.current[`client${sizeProp[axis]}`] < ref.current[`scroll${sizeProp[axis]}`]);
    }, [ref, axis]);
    const setResizeListenerRef = useResizeListener(calcHasScrollbar);
    const setRef = useCallback(node => {
        ref.current = node;
        setResizeListenerRef(ref.current);
        calcHasScrollbar();
    }, [setResizeListenerRef, calcHasScrollbar]);
    return { hasScrollbar, setRef };
}
